import React from "react";
import { AlertTriangle, Copy } from "lucide-react";
import { fallbackCopyTextToClipboard } from "components/utils";

export const MICROK8S_NODEPORT_SERVICE_YAML_SNIPPET = `apiVersion: v1
kind: Service
metadata:
  name: <service-name>
  namespace: <namespace>
spec:
  type: NodePort
  externalTrafficPolicy: Local
  selector:
    app: <app-label>
  ports:
    - name: app-port
      protocol: <TCP|UDP>
      port: <service-port>
      targetPort: <container-port>
      nodePort: <node-port>`;

export const MICROK8S_NODEPORT_EXISTING_SERVICE_SNIPPET = `kubectl patch service <service-name> \\
  --namespace <namespace> \\
  --type=merge \\
  --patch '{"spec":{"externalTrafficPolicy":"Local"}}'`;

interface MicroK8sNodePortNoticeProps {
  className?: string;
}

interface CopyableSnippetProps {
  label: string;
  snippet: string;
}

const CopyableSnippet: React.FC<CopyableSnippetProps> = ({
  label,
  snippet,
}) => (
  <div className="rounded-md bg-neutral-950 p-3 text-neutral-100">
    <div className="mb-2 flex items-center justify-between gap-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-neutral-300">
        {label}
      </p>
      <button
        type="button"
        className="inline-flex shrink-0 items-center gap-1.5 rounded border border-neutral-700 px-2 py-1 text-xs text-neutral-100 hover:border-neutral-500 hover:bg-neutral-900"
        onClick={() => fallbackCopyTextToClipboard(snippet)}
      >
        <Copy className="h-3.5 w-3.5" />
        Copy
      </button>
    </div>
    <pre className="min-w-0 overflow-x-auto whitespace-pre-wrap text-xs">
      <code>{snippet}</code>
    </pre>
  </div>
);

export const MicroK8sNodePortNotice: React.FC<MicroK8sNodePortNoticeProps> = ({
  className = "",
}) => (
  <div
    className={`w-full rounded-md border border-amber-500/40 bg-amber-500/10 p-4 ${className}`}
  >
    <div className="flex items-start gap-3">
      <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-primary">
          For Kubernetes NodePort Services in MicroK8s, set the Service external
          traffic policy to Local before using the NodePort.
        </p>
        <p className="mt-1 text-sm text-secondary">
          Add the policy to your NodePort Service manifest, or patch an existing
          Service after creating it. This applies when using the NodePort from
          either Secure Links or TCP/UDP port access. Use the resulting NodePort
          port, not the pod or container port.
        </p>
        <div className="mt-3 flex flex-col gap-3">
          <CopyableSnippet
            label="Service YAML"
            snippet={MICROK8S_NODEPORT_SERVICE_YAML_SNIPPET}
          />
          <CopyableSnippet
            label="Existing Service"
            snippet={MICROK8S_NODEPORT_EXISTING_SERVICE_SNIPPET}
          />
        </div>
      </div>
    </div>
  </div>
);
